<template>
    <ranking-graph
        :loading="loading"
        :error="error"
        @redo="redo"
        :title="$t('categoryRanking.title')"
        :data="items"
        has-change
        has-total-change
        id-key="id"
        name-key="name"
        total-key="summary"
        :total-type="totalType"
        change-key="summaryChange"
        change-data-key="chart"
        change-data-date-key="date"
        :change-data-value-key="rankingType"
        :export-header="exportHeader"
        :export-rows-unshift="exportRows"
    >
        <template #filters>
            <v-layout align-end justify-start column>
                <v-layout wrap align-center justify-end>
                    <v-flex shrink>
                        <v-select
                            class="d-inline-block mb-3 select--rows"
                            v-model="rowsToShow"
                            @change="handleRowsToShowChange"
                            :items="rowsToShowOptions"
                            :disabled="loading"
                            placeholder=" "
                            :label="$t('categoryRanking.rowsToShow')"
                            outline
                            hide-details
                            dense
                        />
                    </v-flex>
                    <v-flex v-if="hasCampaignFilter" shrink>
                        <select-all
                            class-name="d-inline-block ml-3 mb-3 select--responsive"
                            v-model="campaign"
                            :items="campaignOptions"
                            :disabled="loading"
                            :label="$t('categoryRanking.shop')"
                            :all-label="$t('categoryRanking.allShops')"
                            require-selection
                            @blur="handleCampaignBlur"
                        />
                    </v-flex>
                    <v-flex shrink>
                        <select-all
                            class-name="d-inline-block ml-3 mb-3 select--responsive"
                            v-model="category"
                            :items="categoryOptions"
                            :itemsTree="categoryOptionsTree"
                            :disabled="loading"
                            :label="$t('categorySummary.category')"
                            :all-label="$t('categorySummary.allCategories')"
                            require-selection
                            @blur="handleCategoryBlur"
                        />
                    </v-flex>
                    <v-flex shrink>
                        <v-select
                            class="d-inline-block ml-3 mb-3 select--responsive"
                            v-model="sortBy"
                            @change="handleSortByChange"
                            :items="sortByOptions"
                            :disabled="loading"
                            placeholder=" "
                            :label="$t('categoryRanking.sortBy')"
                            outline
                            hide-details
                            dense
                        />
                    </v-flex>
                    <v-flex shrink>
                        <v-select
                            class="d-inline-block ml-3 mb-3 select--responsive"
                            v-model="rankingType"
                            @change="handleTypeChange"
                            :items="rankingTypeOptions"
                            :disabled="loading"
                            placeholder=" "
                            :label="$t('categoryRanking.rankingType')"
                            outline
                            hide-details
                            dense
                        />
                    </v-flex>
                </v-layout>
                <v-layout wrap align-center justify-end>
                    <v-flex shrink>
                        <v-text-field
                            class="ml-3 mb-3 search"
                            v-model="search"
                            :disabled="loading"
                            :label="$t('categoryRanking.find')"
                            placeholder=" "
                            outline
                            hide-details
                            clearable
                            clear-icon="fa-times"
                            @keyup="handleSearch"
                            @click:clear="handleSearch"
                        />
                    </v-flex>
                </v-layout>
            </v-layout>
        </template>
    </ranking-graph>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import { debounce, deburr, orderBy } from 'lodash';
import RankingGraph from '../../../graphs/RankingGraph.vue';
import eventBus from '../../../../services/eventBus';
import SelectAll from '../../../common/SelectAll.vue';
import campaignFilterMixin from '../../../../mixins/campaignFilterMixin';
import categoryFilterMixin from '../../../../mixins/categoryFilterMixin';
import exportService from '../../../../services/exportService';

export default {
    name: 'category-ranking',
    components: {
        SelectAll,
        RankingGraph,
    },
    mixins: [campaignFilterMixin, categoryFilterMixin],
    props: {
        dateFilter: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            region: process.env.VUE_APP_REGION_MODE,
            data: null,
            items: [],
            search: null,
            rowsToShow: 10,
            rowsToShowOptions: [
                {
                    value: 10,
                    text: '10',
                },
                {
                    value: 20,
                    text: '20',
                },
                {
                    value: 50,
                    text: '50',
                },
            ],
            rankingType: 'impressions',
            rankingTypeOptions: [
                {
                    value: 'impressions',
                    text: this.$t('categoryRanking.impressionsOffer'),
                },
                {
                    value: 'clicks',
                    text: this.$t('categoryRanking.clicks'),
                },
                {
                    value: 'quantity',
                    text: this.$t('categoryRanking.quantity'),
                },
                {
                    value: 'amount',
                    text: this.$t('categoryRanking.amount'),
                },
            ],
            totalType: 'decimal',
            sortBy: 'summary',
            sortByOptions: [
                {
                    value: 'summary',
                    text: this.$t('categoryRanking.sortBySummary'),
                },
                {
                    value: 'change',
                    text: this.$t('categoryRanking.sortByChange'),
                },
            ],
            error: null,
        };
    },
    computed: {
        loading() {
            return this.rankingLoading;
        },
        hasCampaignFilter() {
            return this.region !== 'PL' || (this.rankingType !== 'quantity' && this.rankingType !== 'amount');
        },
        exportRows() {
            return [
                `"${exportService.parseText(this.dateFilter.startDate)} - ${exportService.parseText(this.dateFilter.endDate)}"`,
                `"${exportService.parseText(new Date().toLocaleDateString())}"`,
                `"${exportService.parseText(this.campaign.map(id => this.getCampaignNameFromId(id)).join(', '))}"`,
            ];
        },
        exportHeader() {
            const valueLabel = this.rankingTypeOptions.find(option => option.value === this.rankingType).text;
            const valueLabelPostfix = this.rankingType === 'amount' ? ` [${process.env.VUE_APP_SYSTEM_CURRENCY}]` : '';
            return [
                `${this.$t('dataFor')}`,
                `${this.$t('exportingOn')}`,
                `${this.$t('selectedStores')}`,
                `${this.$t('categoryRanking.exportName')}`,
                `${valueLabel}${valueLabelPostfix}`,
                `${valueLabel} (% ${this.$t('change')})`,
            ];
        },
        ...mapState({
            rankingLoading: state => state.reportsCategory.loading.ranking,
        }),
        ...mapGetters({
            getterRanking: 'reportsCategory/getRanking',
        }),
    },
    watch: {
        dateFilter(newValue, oldValue) {
            if (
                newValue.startDate !== oldValue.startDate
                || newValue.endDate !== oldValue.endDate
                || newValue.compareStartDate !== oldValue.compareStartDate
                || newValue.compareEndDate !== oldValue.compareEndDate
            ) {
                this.getData();
            }
        },
    },
    created() {
        this.setDefaultCategory();
        this.setDefaultCampaign();
        this.getData();
        eventBus.$on('resetFilters', this.reset);
    },
    beforeDestroy() {
        eventBus.$off('resetFilters', this.reset);
    },
    methods: {
        reset() {
            this.setDefaultCategory();
            this.setDefaultCampaign();
            this.rowsToShow = 10;
            this.search = null;
            this.rankingType = 'impressions';
            this.sortBy = 'summary';
            this.getData();
        },
        emitFilterDefaultChange() {
            const isFilterDefault = !this.search
                && this.rankingType === 'impressions'
                && this.rowsToShow === 10
                && this.sortBy === 'summary'
                && this.isDefaultCampaign(['PL'])
                && this.isDefaultCategory();
            this.$emit('filterDefaultChange', isFilterDefault);
        },
        getData(force = false) {
            this.emitFilterDefaultChange();
            this.getRanking(force);
        },
        setDefaultCategory() {
            this.category = this.getterCategoryOptions.map(category => category.value);
        },
        handleCategoryBlur() {
            this.getData();
        },
        handleRowsToShowChange() {
            this.getData();
        },
        handleCampaignBlur() {
            this.getData();
        },
        handleTypeChange() {
            this.getData();
            this.totalType = this.rankingType === 'amount' ? 'currency' : 'decimal';
        },
        handleSortByChange() {
            this.getData();
        },
        redo() {
            this.getData(true);
        },
        handleSearch: debounce(function handleSearch() {
            this.setItems();
        }, 500),
        setItems() {
            this.emitFilterDefaultChange();
            if (!this.data) {
                this.items = [];
            }

            let { data } = this;

            if (this.search) {
                const search = deburr(this.search).toLowerCase();
                data = data.filter(item => deburr(item.name)
                    .toLowerCase()
                    .includes(search));
            }

            const sortField = this.sortBy === 'summary' ? 'summary' : 'summaryChange';
            data = orderBy(data, [sortField], ['desc']);

            this.items = data.slice(0, this.rowsToShow);
        },
        async getRanking(force = false) {
            try {
                const {
                    startDate, endDate, compareStartDate, compareEndDate,
                } = this.dateFilter;
                const campaign = this.hasCampaignFilter ? this.campaign : [];
                this.error = null;
                await this.fetchRanking({
                    force,
                    startDate,
                    endDate,
                    compareStartDate,
                    compareEndDate,
                    rankingType: this.rankingType,
                    campaign,
                    category: this.category,
                });
                this.data = this.getterRanking(startDate, endDate, compareStartDate, compareEndDate, this.rankingType, campaign, this.category);
                this.setItems();
            } catch (e) {
                this.error = e.message;
            }
        },
        ...mapActions({
            fetchRanking: 'reportsCategory/fetchRanking',
        }),
    },
};
</script>

<style lang="scss" scoped>
.select--rows {
    width: 120px;
}
</style>
